// GMPCertification.jsx
import React from "react";

export default function GMPCertification() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Content */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            GMP Certification
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            GMP Certification Support for Manufacturers
          </h1>
          <div className="w-20 h-1 bg-emerald-600 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks supports GMP Certification for manufacturing
            quality compliance, helping facilities align with recognised Good
            Manufacturing Practice expectations for their sector.[web:426][web:429]
          </p>
          <p className="text-gray-700">
            The focus is on structuring processes, documentation, premises, and
            records so that inspections and audits can verify consistent,
            controlled manufacturing.[web:421][web:423]
          </p>
        </div>

        {/* Right: Image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-emerald-500 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/3825536/pexels-photo-3825536.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Pharmaceutical or food manufacturing line showing controlled production"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* DOCUMENTS & WHY CHOOSE US */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Documents Required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for GMP Certification
            </h2>
            <p className="text-gray-700 mb-4">
              Auditors and regulators typically expect clear process,
              quality-system, and facility information to demonstrate GMP
              control.[web:421][web:423][web:429]
            </p>
            <div className="bg-gray-50 border border-emerald-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Manufacturing process details, including flow charts,
                  validated process descriptions, and batch manufacturing and
                  packaging records for each product.[web:421][web:422][web:423]
                </li>
                <li>
                  Quality manuals and SOPs covering documentation control,
                  equipment cleaning and maintenance, personnel hygiene, raw
                  material control, in‑process checks, testing, deviations and
                  CAPA.[web:421][web:422][web:429]
                </li>
                <li>
                  Facility layout and premises information showing room
                  classification, material and personnel flows, utilities, and
                  measures to prevent mix‑ups and contamination.[web:421][web:423]
                </li>
                <li>
                  Supporting records such as training logs, equipment
                  qualification/validation documents, environmental monitoring,
                  and release/testing reports.[web:422][web:423][web:429]
                </li>
              </ul>
            </div>
          </div>

          {/* Why Choose Hawkins & Brooks */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The service is oriented around turning GMP rules into workable
              shop‑floor practices and clear, audit‑ready documentation.[web:422][web:423]
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-emerald-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  GMP Regulatory Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Guidance on applicable GMP frameworks (such as WHO GMP or
                  local GMP codes) and how they translate into requirements for
                  premises, equipment, personnel, and records.[web:426][web:429]
                </p>
              </div>

              <div className="border-l-4 border-emerald-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Compliance-Focused Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Help in closing gaps found during pre‑audit reviews, shaping
                  SOPs and records so they match what inspectors typically look
                  for.[web:421][web:423]
                </p>
              </div>

              <div className="border-l-4 border-emerald-600 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Advisory
                </h3>
                <p className="text-gray-700 text-sm">
                  Ongoing input through implementation, mock audits, official
                  inspections and follow‑up, supporting long‑term GMP
                  maintenance.[web:423][web:429]
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
              >
                Get Consultation
              </a>
              <a
                href="tel:+919217468906"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-emerald-600 text-emerald-700 font-medium hover:bg-emerald-50 transition-colors text-sm"
              >
                Call Hawkins &amp; Brooks
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
